import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Eye, EyeOff, AlertCircle, CheckCircle, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_CONFIG = {
    instagram: {
        name: 'Instagram',
        emoji: '📸',
        color: 'from-purple-500 to-pink-500',
        placeholder_user: 'Username or email',
        placeholder_pass: 'Password'
    },
    tiktok: {
        name: 'TikTok',
        emoji: '🎵',
        color: 'from-black to-gray-700',
        placeholder_user: 'Email or phone',
        placeholder_pass: 'Password'
    }
};

export default function SessionLoginModal({ 
    platform, 
    isOpen, 
    onClose, 
    onSuccess, 
    api 
}) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const config = PLATFORM_CONFIG[platform];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!username || !password) {
            setError('Please enter both username and password');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.post(`/session-auth/login/${platform}`, {
                platform: platform,
                username: username,
                password: password
            });

            if (response.data.success) {
                setSuccess(true);
                toast.success(response.data.message);
                setTimeout(() => {
                    onSuccess();
                    handleClose();
                }, 1500);
            } else {
                setError(response.data.error || 'Login failed');
                
                if (response.data.requires_2fa) {
                    setError('Two-factor authentication is enabled. Please disable it temporarily or use an app-specific password.');
                }
                if (response.data.requires_captcha) {
                    setError('Captcha verification required. Please try again in a few minutes.');
                }
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setUsername('');
        setPassword('');
        setError(null);
        setSuccess(false);
        setShowPassword(false);
        onClose();
    };

    if (!config) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <span className={`text-2xl p-2 rounded-lg bg-gradient-to-r ${config.color} text-white`}>
                            {config.emoji}
                        </span>
                        <span>Sign in to {config.name}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Enter your {config.name} credentials to connect your account
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    {/* Security Notice */}
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="flex items-start gap-2">
                            <Lock className="h-4 w-4 text-primary mt-0.5" />
                            <div className="text-xs text-muted-foreground">
                                <p className="font-medium text-foreground mb-1">Secure Connection</p>
                                <p>Your credentials are used only to establish a session and are never stored. Only encrypted session tokens are saved.</p>
                            </div>
                        </div>
                    </div>

                    {/* Username Field */}
                    <div className="space-y-2">
                        <Label htmlFor="username">Username / Email</Label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                id="username"
                                type="text"
                                placeholder={config.placeholder_user}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="pl-10"
                                disabled={loading || success}
                                data-testid="session-username-input"
                            />
                        </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder={config.placeholder_pass}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="pl-10 pr-10"
                                disabled={loading || success}
                                data-testid="session-password-input"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                                <p className="text-sm text-destructive">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <p className="text-sm text-green-500">Successfully connected!</p>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <Button 
                        type="submit" 
                        className={`w-full bg-gradient-to-r ${config.color} hover:opacity-90`}
                        disabled={loading || success}
                        data-testid="session-login-submit"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Signing in...
                            </>
                        ) : success ? (
                            <>
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Connected!
                            </>
                        ) : (
                            `Sign in to ${config.name}`
                        )}
                    </Button>
                </form>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <p className="text-xs text-muted-foreground text-center w-full">
                        By signing in, you authorize KyberSocial to post on your behalf.
                        You can disconnect anytime from the Accounts page.
                    </p>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
